import React, { useContext, useEffect, useRef, useState } from 'react';
import { ContentTrackerProps } from './types';
import { FullScreenTrackingContext } from './FullScreenTrackingContext';
import { ImageViewerProps } from '../../Image/ImageViewer';
import { WindowFocusContext } from './WindowFocusContext';
import { TrackEvent } from '../../../enums/Content/TrackEvent';
import FullScreenImageViewer, {
  FullScreenImageViewerProps,
} from '../../Image/FullScreenImageViewer';
import SimpleTracker from '../../../Services/Analytics/SimpleTracker';
import TrackInterface from '../../../graphql/Rooms/TrackInterface';
import useIntersectionObserver from '../../../lib/hook/useIntersectionObserver';

const IMAGE_VISIBILITY_THRESHOLD = 0.65;

type PickedImageViewerProps = Pick<
  FullScreenImageViewerProps,
  'downloadTrigger' | 'trackingProperties'
>;

export interface ImageTrackerProps
  extends ContentTrackerProps,
    ImageViewerProps,
    PickedImageViewerProps {
  onLoadWithinViewport?: () => void;
  title?: string;
}

const ImageTracker: React.FC<ImageTrackerProps> = (
  props: ImageTrackerProps
): JSX.Element => {
  const { fullScreenContentId, setFullScreenContentId } = useContext(
    FullScreenTrackingContext
  );
  const { isWindowFocused } = useContext(WindowFocusContext);

  const {
    contactId,
    contentId,
    onLoad: onLoadProp,
    onLoadWithinViewport,
    roomId,
    title,
    track,
    trackingProperties,
    version,
    ...imageViewerProps
  } = props;

  const [loaded, setLoaded] = useState(false);
  const [withinViewport, setWithinViewport] = useState(false);

  const { current: tracker } = useRef(new SimpleTracker());

  const ref = useIntersectionObserver<HTMLDivElement>(
    (isIntersecting: boolean) => setWithinViewport(isIntersecting),
    IMAGE_VISIBILITY_THRESHOLD
  );

  useEffect(() => {
    if (track) {
      tracker.attach(trackEvent);
    }

    return () => tracker.detach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This use effect makes sure that whenever the following rules are met:
   *  1. Must be tracked
   *  2. Is within viewport
   *  3. The window/tab must be focused in order to prevent tracking while the tab
   *     is loading in the background or the user jumps to another window
   *  4. There are no elements in full screen OR if there's something in
   *     full screen, it IS the current content
   * Then the content is tracked. This is to avoid different images that
   * are within the viewport to be tracked whilst the user is focusing
   * on one in specific, when they have the full screen view open
   */
  useEffect(() => {
    const shouldTrackBecauseFullScreen =
      !fullScreenContentId || fullScreenContentId === contentId;

    const isActiveInViewport =
      withinViewport && isWindowFocused && shouldTrackBecauseFullScreen;
    const shouldTrackView = loaded && isActiveInViewport;

    tracker.setActive(!!track && isActiveInViewport);

    if (shouldTrackView && onLoadWithinViewport) {
      onLoadWithinViewport();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenContentId, isWindowFocused, loaded, withinViewport]);

  const trackEvent = (elapsedTime: number) => {
    if (!(track && roomId && version)) {
      return;
    }

    try {
      new TrackInterface(contactId).track({
        contentId,
        event: TrackEvent.IMAGES_VIEWED_SECONDS,
        measure: elapsedTime,
        metadata: undefined,
        roomId,
        version,
      });
    } catch (e) {
      // user shouldn't be alerted about this fail, but we're catching
      // this exception to avoid "Uncaught promise reject" warnings
    }
  };

  const onFullScreenChange = (isFullScreen: boolean) => {
    setFullScreenContentId(isFullScreen ? contentId : null);
  };

  const handleLoad = () => {
    setLoaded(true);
    if (onLoadProp) {
      onLoadProp();
    }
  };

  return (
    <FullScreenImageViewer
      {...imageViewerProps}
      onLoad={handleLoad}
      onFullScreenChange={onFullScreenChange}
      ref={ref}
      title={title}
      trackingProperties={trackingProperties}
      zoomable
    />
  );
};

export default ImageTracker;
